import React from 'react';
import { Formik, Form, Field } from 'formik';
import useCreateAccount from './hooks/useCreateAccount';
import { useNavigate } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { toast } from 'sonner';
import PageShell from '@/src/Common/components/PageShell';

interface CreateAccountFormValues {
    firstName: string;
    lastName: string;
    username: string;
    email: string;
    password: string;
    orgId: number;
    role: number;
}

export default function CreateAccount() {
    const { orgId } = useUserOrg();
    const initialValues: CreateAccountFormValues = {
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        password: '',
        orgId,
        role: 1,
    };
    const navigate = useNavigate();
    const { createAccount } = useCreateAccount();

    return (
        <PageShell
            title="Create Account"
            breadcrumbs={[
                { label: 'Accounts', href: '/accounts' },
                { label: 'Create' },
            ]}
        >
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, actions) => {
                    const response = await createAccount(values);
                    actions.setSubmitting(false);

                    if (response?.data.id) {
                        toast.success('Account created successfully');
                        navigate('/accounts');
                    } else {
                        toast.error(
                            `Account creation failed: ${response.message}`,
                        );
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="space-y-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <Card>
                            <CardHeader>
                                <CardTitle>Account Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="username">Username</Label>
                                        <Input
                                            id="username"
                                            name="username"
                                            value={props.values.username}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            type="email"
                                            id="email"
                                            name="email"
                                            placeholder="yo@email.com"
                                            value={props.values.email}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="firstName">First Name</Label>
                                        <Input
                                            id="firstName"
                                            name="firstName"
                                            value={props.values.firstName}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="lastName">Last Name</Label>
                                        <Input
                                            id="lastName"
                                            name="lastName"
                                            value={props.values.lastName}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="password">Password</Label>
                                        <Input
                                            id="password"
                                            type="password"
                                            name="password"
                                            value={props.values.password}
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="role">Role</Label>
                                        <Select
                                            value={String(props.values.role)}
                                            onValueChange={(value) => {
                                                props.setFieldValue('role', +value);
                                            }}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="2">Client</SelectItem>
                                                <SelectItem value="1">User</SelectItem>
                                                <SelectItem value="0">Admin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <div className="flex items-center justify-end gap-4">
                            <NavLink to="/accounts">
                                <Button type="button" variant="outline">Cancel</Button>
                            </NavLink>
                            <Button type="submit">
                                Create Account
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </PageShell>
    );
}
